import React, { useState, useEffect } from 'react';
import FocusTrap from '../a11y/FocusTrap';

/**
 * NoteEditor — slide-in note editor panel.
 *
 * Accepts context anchor either via the legacy timestampSec/slideIndex props
 * OR via the newer contextAnchor prop: { type: 'timestamp'|'slide', value: number }.
 * contextAnchor takes precedence when both are provided.
 */
export default function NoteEditor({
  isOpen, onClose, onSave,
  sceneId, tutorialId, domain,
  timestampSec = null, slideIndex = null,
  contextAnchor = null,
  existingNote = null,
}) {
  // Resolve contextAnchor → timestampSec / slideIndex
  const resolvedTimestampSec = contextAnchor?.type === 'timestamp'
    ? contextAnchor.value
    : timestampSec;
  const resolvedSlideIndex = contextAnchor?.type === 'slide'
    ? contextAnchor.value
    : slideIndex;

  const [text, setText] = useState(existingNote?.text || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setText(existingNote?.text || '');
  }, [existingNote]);

  const handleSave = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await onSave({
        scene_id: sceneId,
        tutorial_id: tutorialId,
        domain,
        text: text.trim(),
        timestamp_sec: resolvedTimestampSec,
        slide_index: resolvedSlideIndex,
        note_type: 'note',
      });
      setText('');
      onClose();
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <FocusTrap active={isOpen}>
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Note editor"
      style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 340,
        background: '#ffffff',
        color: '#1e293b',
        borderLeft: '1px solid #d1d5db',
        boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
        zIndex: 200, display: 'flex', flexDirection: 'column',
        animation: 'slideInRight 0.2s ease-out',
      }}
    >
      <div style={{
        padding: '12px 16px', borderBottom: '1px solid #d1d5db',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1e293b' }}>
          {existingNote ? 'Edit Note' : 'Add Note'}
          {resolvedTimestampSec != null && ` @ ${Math.floor(resolvedTimestampSec / 60)}:${String(Math.floor(resolvedTimestampSec % 60)).padStart(2, '0')}`}
          {resolvedSlideIndex != null && ` — Slide ${resolvedSlideIndex + 1}`}
        </span>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer',
          color: '#6b7280',
        }}>×</button>
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Type your note here..."
        autoFocus
        style={{
          flex: 1, padding: 16, border: 'none', outline: 'none', resize: 'none',
          fontFamily: 'inherit', fontSize: '0.875rem', lineHeight: 1.6,
          background: '#fafbfc', color: '#1e293b',
        }}
      />
      <div style={{
        padding: '12px 16px', borderTop: '1px solid #d1d5db',
        display: 'flex', gap: 8, justifyContent: 'flex-end',
      }}>
        <button onClick={onClose} style={{
          padding: '6px 16px', borderRadius: 6, border: '1px solid #d1d5db',
          background: 'transparent', cursor: 'pointer', fontSize: '0.8rem',
          color: '#475569',
        }}>Cancel</button>
        <button onClick={handleSave} disabled={!text.trim() || saving} style={{
          padding: '6px 16px', borderRadius: 6, border: 'none',
          background: text.trim() ? '#3b82f6' : '#d1d5db',
          color: '#fff', cursor: text.trim() ? 'pointer' : 'default',
          fontWeight: 600, fontSize: '0.8rem',
        }}>
          {saving ? 'Saving...' : 'Save Note'}
        </button>
      </div>
    </div>
    </FocusTrap>
  );
}
